import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Sentry from '@sentry/react';

import { store, resetAllStores } from "../../store";
import { queryClient } from "../../services/react-query/react-query.client";
import { stopAuthenticatedReplay } from "../../services/sentry-replay.service";

// Ends the session as soon as the API answers 401.
//
// The http interceptors dispatch a `sessionExpired` window event on every 401.
// This used to open a dialog and wait for a click while the dead token stayed
// in the store, so every mount-time request kept firing with it, each 401
// re-dispatched the event, and the settings gate remounted the shell on every
// retry (see use-effective-settings.ts). One expired tab produced ~30 req/s
// until the WAF rate limit blocked the client (2026-09-08).
//
// Now: clear auth and every store, drop the persisted state and the query
// cache so the next login starts clean, tell the user, and go to /login. The listener runs once
// per mount because a burst of in-flight 401s fires the event many times.
export const SessionExpiredModal: React.FC = () => {
    const navigate = useNavigate();
    const handled = useRef(false);

    useEffect(() => {
        const handleSessionExpired = () => {
            if (handled.current) {
                return;
            }
            handled.current = true;

            store.dispatch.auth.logout();
            Sentry.setUser(null);
            stopAuthenticatedReplay();
            // Drop every subscription-scoped store as well (same as the
            // sidebar logout and the masquerade listener) so a re-login as
            // another user in this tab cannot render the previous tenant's
            // data before its own fetches land.
            resetAllStores();
            localStorage.removeItem('pom-admin-core-persistor');
            queryClient.clear();

            toast('Your session has expired. Please log in again.', {
                type: 'info',
                autoClose: 5000,
            });

            // Preserve the deep link (Pollen review, round 4 — "Medium #2"):
            // same `state.from` shape as RedirectToLoginPreservingPath
            // (routes/index.tsx) and step-login-form.tsx's read of it — a
            // POM Master with a stale token clicking a Zendesk deep link now
            // lands back on the chat after logging in again, not on '/'.
            //
            // Scoped to `/support-agent` only (Pollen review, round 5, item
            // 3): this is the ONE path this feature exists for. Every other
            // path getting `from` meant a first-login user returned to
            // wherever their stale token happened to expire (often a blank
            // page mid-render), and a different person logging in on the
            // same tab landed on the PREVIOUS user's path instead of '/'.
            const from = window.location.pathname.startsWith('/support-agent')
                ? `${window.location.pathname}${window.location.search}`
                : undefined;
            navigate('/login', { replace: true, ...(from ? { state: { from } } : {}) });
        };

        window.addEventListener('sessionExpired', handleSessionExpired);

        return () => {
            window.removeEventListener('sessionExpired', handleSessionExpired);
        };
    }, [navigate]);

    return null;
};
